import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  FormControlLabel,
  IconButton,
  Link,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LocalPhoneOutlinedIcon from "@mui/icons-material/LocalPhoneOutlined";
import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import { ElementType, useEffect, useState } from "react";
import { DragDropContext, Draggable, DropResult } from "react-beautiful-dnd";
import StrictModeDroppable from "global/components/StrictModeDroppable/StrictModeDroppable";
import {
  AttributeMappingErrors,
  IDENTITY_ATTRIBUTE_FIELDS,
  IdentityAttributeKey,
  IdentityAttributeMapping,
  MAX_ATTRIBUTE_PATH_LENGTH,
  SYNC_ELIGIBLE_ATTRIBUTE_FIELDS,
  SourceMode,
} from "./SsoConfigTypes";
import {
  attrMappingDefaultChipSx,
  attrMappingDragHandleSx,
  attrMappingErrorSx,
  attrMappingFieldIconSx,
  attrMappingModeOptionSx,
  attrMappingPathIndexSx,
  attrMappingPathRowSx,
  attrMappingRequiredChipSx,
  attrMappingRowSx,
  attrMappingSectionIconSx,
} from "./EnterpriseSSO.styles";

const CLAIM_FONT_FAMILY = "monospace";

const EMAIL_FIELD = IDENTITY_ATTRIBUTE_FIELDS.find((field) => field.key === "email")!;

const FIELD_ICONS: Record<IdentityAttributeKey, ElementType> = {
  email: MailOutlineIcon,
  displayName: PersonOutlineIcon,
  firstName: PersonOutlineIcon,
  lastName: PersonOutlineIcon,
  phone: LocalPhoneOutlinedIcon,
};

const MODE_COPY: Record<SourceMode, { title: string }> = {
  default: { title: "Default" },
  custom: { title: "Custom" },
};

/**
 * Rows carry a generated id rather than using their value or index as a key: values are free text
 * and may be blank or duplicated mid-edit, and an index-keyed row loses input focus the moment a
 * drag reorders it. The counter is module-level because these ids only need to be unique within a
 * page, never stable across mounts.
 */
let rowSeq = 0;

interface PathRow {
  id: string;
  value: string;
}

const toRows = (values: string[]): PathRow[] =>
  values.map((value) => ({ id: `attr-path-${(rowSeq += 1)}`, value }));

const sameValues = (rows: PathRow[], values: string[]): boolean =>
  rows.length === values.length && rows.every((row, index) => row.value === values[index]);

interface AttributePathListProps {
  fieldKey: IdentityAttributeKey;
  label: string;
  suggestions: string[];
  paths: string[];
  disabled: boolean;
  onChange: (next: string[]) => void;
}

/**
 * The ordered list an admin drags to set precedence. Order is the whole point — "first non-empty
 * value wins" is positional — so the rows are numbered and draggable rather than being chips, whose
 * order reads as incidental.
 *
 * Rows are held locally and pushed up on every change: an in-progress row is legitimately empty, and
 * the parent's mapping cannot represent that without treating "" as a configured path. The effect
 * re-seeds only when the two genuinely diverge (dialog reopen, reset, mode switch), so typing does
 * not fight the sync.
 */
const AttributePathList = ({
  fieldKey,
  label,
  suggestions,
  paths,
  disabled,
  onChange,
}: AttributePathListProps) => {
  const [rows, setRows] = useState<PathRow[]>(() => toRows(paths));

  useEffect(() => {
    setRows((prev) => (sameValues(prev, paths) ? prev : toRows(paths)));
  }, [paths]);

  const commit = (next: PathRow[]) => {
    setRows(next);
    onChange(next.map((row) => row.value));
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    const next = [...rows];
    const [moved] = next.splice(result.source.index, 1);
    next.splice(result.destination.index, 0, moved);
    commit(next);
  };

  const handleEdit = (id: string, value: string) =>
    commit(rows.map((row) => (row.id === id ? { ...row, value } : row)));

  const handleRemove = (id: string) => commit(rows.filter((row) => row.id !== id));

  const handleAdd = () => commit([...rows, ...toRows([""])]);

  // Already-listed values are dropped from the suggestions so the menu only ever offers something
  // that would actually change the list.
  const available = suggestions.filter(
    (suggestion) => !rows.some((row) => row.value === suggestion),
  );

  return (
    <Box>
      <Typography
        variant="caption"
        sx={{ fontWeight: 700, color: "text.secondary", display: "block", mb: 0.75 }}
      >
        Attribute order{" "}
        <Box component="span" sx={{ fontWeight: 400, color: "text.disabled" }}>
          (first non-empty value wins)
        </Box>
      </Typography>

      <DragDropContext onDragEnd={handleDragEnd}>
        <StrictModeDroppable droppableId={`attr-paths-${fieldKey}`}>
          {(dropProvided: any) => (
            <Stack
              spacing={0.75}
              ref={dropProvided.innerRef}
              {...dropProvided.droppableProps}
            >
              {rows.map((row, index) => (
                <Draggable
                  key={row.id}
                  draggableId={row.id}
                  index={index}
                  isDragDisabled={disabled || rows.length < 2}
                >
                  {(dragProvided: any, dragSnapshot: any) => (
                    <Box
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                      sx={attrMappingPathRowSx(dragSnapshot.isDragging)}
                    >
                      <Box
                        {...dragProvided.dragHandleProps}
                        sx={attrMappingDragHandleSx(disabled || rows.length < 2)}
                        aria-label={`Reorder ${label} attribute ${index + 1}`}
                      >
                        <DragIndicatorIcon sx={{ fontSize: 18 }} />
                      </Box>
                      <Typography component="span" sx={attrMappingPathIndexSx}>
                        {index + 1}
                      </Typography>
                      <Autocomplete
                        freeSolo
                        fullWidth
                        disableClearable
                        size="small"
                        disabled={disabled}
                        options={available}
                        value={row.value}
                        onChange={(_event, next) => handleEdit(row.id, (next as string) ?? "")}
                        onInputChange={(_event, next, reason) => {
                          if (reason === "input") handleEdit(row.id, next);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            hiddenLabel
                            variant="outlined"
                            placeholder="Attribute name"
                            inputProps={{
                              ...params.inputProps,
                              maxLength: MAX_ATTRIBUTE_PATH_LENGTH,
                              "aria-label": `${label} attribute ${index + 1}`,
                            }}
                            // Same box every other themed input uses (see CustomInput) — just with
                            // the monospace claim font this dialog already uses for attribute paths.
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                                minHeight: "2.5rem",
                              },
                              "& .MuiOutlinedInput-input": {
                                fontFamily: CLAIM_FONT_FAMILY,
                                fontSize: "14px",
                                fontWeight: 500,
                                padding: "0.4375rem",
                              },
                            }}
                          />
                        )}
                      />
                      <Tooltip title="Remove" describeChild>
                        <span>
                          <IconButton
                            size="small"
                            disabled={disabled}
                            onClick={() => handleRemove(row.id)}
                            aria-label={`Remove ${label} attribute ${index + 1}`}
                          >
                            <CloseIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  )}
                </Draggable>
              ))}
              {dropProvided.placeholder}
            </Stack>
          )}
        </StrictModeDroppable>
      </DragDropContext>

      <Button
        size="small"
        startIcon={<AddIcon fontSize="small" />}
        onClick={handleAdd}
        disabled={disabled}
        sx={{ mt: 0.75, textTransform: "none", fontWeight: 600 }}
      >
        Add attribute
      </Button>
    </Box>
  );
};

interface DefaultPreviewProps {
  suggestions: string[];
}

/**
 * Default mode still shows what the server will try, read-only. Hiding it entirely would make
 * "Default" an unexplained black box that an admin can only inspect by switching to Custom — which
 * is the one action that stops it being the default.
 */
const DefaultPreview = ({ suggestions }: DefaultPreviewProps) => (
  <Box>
    <Typography
      variant="caption"
      sx={{ fontWeight: 700, color: "text.secondary", display: "block", mb: 0.75 }}
    >
      We try these, in order
    </Typography>
    {suggestions.length > 0 ? (
      <Stack direction="row" flexWrap="wrap" gap={0.75}>
        {suggestions.map((suggestion) => (
          <Chip key={suggestion} size="small" label={suggestion} sx={attrMappingDefaultChipSx} />
        ))}
      </Stack>
    ) : (
      <Typography variant="caption" sx={{ color: "text.disabled" }}>
        Defaults are unavailable right now. Switch to Custom to set the attributes yourself.
      </Typography>
    )}
  </Box>
);

interface AttributeMappingEditorProps {
  value: IdentityAttributeMapping;
  defaults: Partial<Record<IdentityAttributeKey, string[]>>;
  enabledFields: IdentityAttributeKey[];
  sourceMode: Record<IdentityAttributeKey, SourceMode>;
  expandedFields: Partial<Record<IdentityAttributeKey, boolean>>;
  errors: AttributeMappingErrors;
  onChange: (next: IdentityAttributeMapping) => void;
  onEnabledFieldsChange: (next: IdentityAttributeKey[]) => void;
  onSourceModeChange: (key: IdentityAttributeKey, mode: SourceMode) => void;
  onExpandedFieldsChange: (next: Partial<Record<IdentityAttributeKey, boolean>>) => void;
  disabled?: boolean;
}

/**
 * Two groups, deliberately separated: Email is how we find the account and is never a sync target,
 * so it carries a Required chip instead of a toggle — the backend rejects "email" in
 * syncEnabledFields outright, and a switch that cannot be switched is worse than no switch.
 * Every other field is off by default and collapses to a single line when off, so an admin scanning
 * the dialog sees "nothing syncs" at a glance instead of five identical open panels.
 *
 * All state lives in the parent. The editor unmounts whenever the dialog's Roles tab is selected,
 * and a half-finished Custom row must survive that round trip.
 */
const AttributeMappingEditor = ({
  value,
  defaults,
  enabledFields,
  sourceMode,
  expandedFields,
  errors,
  onChange,
  onEnabledFieldsChange,
  onSourceModeChange,
  onExpandedFieldsChange,
  disabled = false,
}: AttributeMappingEditorProps) => {
  const handlePathsChange = (key: IdentityAttributeKey, paths: string[]) => {
    const next: IdentityAttributeMapping = { ...value };
    if (paths.length === 0) {
      delete next[key];
    } else {
      next[key] = paths;
    }
    onChange(next);
  };

  const toggleEnabled = (key: IdentityAttributeKey, checked: boolean) => {
    const eligibleKeys = SYNC_ELIGIBLE_ATTRIBUTE_FIELDS.map((field) => field.key);
    const set = new Set(enabledFields.filter((k) => eligibleKeys.includes(k)));
    if (checked) {
      set.add(key);
      onExpandedFieldsChange({ ...expandedFields, [key]: true });
    } else {
      set.delete(key);
    }
    onEnabledFieldsChange(eligibleKeys.filter((k) => set.has(k)));
  };

  const setMode = (key: IdentityAttributeKey, mode: SourceMode) => {
    onSourceModeChange(key, mode);
    if (mode === "default") {
      handlePathsChange(key, []);
    } else if ((value[key] ?? []).length === 0) {
      // Custom opens with one empty row rather than an empty list: the admin picked Custom in order
      // to type something, and an empty panel with only an "Add attribute" link makes them click twice.
      handlePathsChange(key, [""]);
    }
  };

  const toggleExpanded = (key: IdentityAttributeKey) =>
    onExpandedFieldsChange({ ...expandedFields, [key]: !expandedFields[key] });

  const renderModeChoice = (key: IdentityAttributeKey, mode: SourceMode) => (
    <Box>
      <Typography
        variant="caption"
        sx={{ fontWeight: 700, color: "text.secondary", display: "block", mb: 0.75 }}
      >
        Mapping type
      </Typography>
      <RadioGroup
        row
        value={mode}
        onChange={(_event, next) => setMode(key, next as SourceMode)}
        sx={{ gap: 1, flexWrap: "nowrap" }}
      >
        {(Object.keys(MODE_COPY) as SourceMode[]).map((option) => (
          <FormControlLabel
            key={option}
            value={option}
            disabled={disabled}
            sx={attrMappingModeOptionSx(mode === option)}
            control={<Radio size="small" sx={{ py: 0, mr: 0.5 }} />}
            label={
              <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                {MODE_COPY[option].title}
              </Typography>
            }
          />
        ))}
      </RadioGroup>
    </Box>
  );

  const renderModeBody = (key: IdentityAttributeKey, label: string, helper: string) => {
    const mode = sourceMode[key] ?? "default";
    const fallback = defaults[key] ?? [];
    const error = errors[key];
    return (
      <Stack spacing={1.5}>
        {renderModeChoice(key, mode)}
        {mode === "custom" ? (
          <AttributePathList
            fieldKey={key}
            label={label}
            suggestions={fallback}
            paths={value[key] ?? []}
            disabled={disabled}
            onChange={(next) => handlePathsChange(key, next)}
          />
        ) : (
          <DefaultPreview suggestions={fallback} />
        )}
        {error && (
          <Typography variant="caption" sx={attrMappingErrorSx}>
            {error}
          </Typography>
        )}
        {helper && (
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
            {helper}
          </Typography>
        )}
      </Stack>
    );
  };

  const enabledSyncKeys = SYNC_ELIGIBLE_ATTRIBUTE_FIELDS.map((field) => field.key).filter((key) =>
    enabledFields.includes(key),
  );
  const allExpanded =
    enabledSyncKeys.length > 0 && enabledSyncKeys.every((key) => expandedFields[key]);

  const toggleExpandAll = () => {
    const next = !allExpanded;
    const copy = { ...expandedFields };
    enabledSyncKeys.forEach((key) => {
      copy[key] = next;
    });
    onExpandedFieldsChange(copy);
  };

  const EmailIcon = FIELD_ICONS.email;
  // Undefined reads as expanded so the box opens by default on first load, before the panel
  // has had a chance to seed expandedFields.
  const emailExpanded = expandedFields.email ?? true;
  const toggleEmailExpanded = () => toggleExpanded(EMAIL_FIELD.key);

  return (
    <Stack spacing={3.5}>
      <Box>
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
          <LockOutlinedIcon sx={attrMappingSectionIconSx} />
          <Typography
            variant="overline"
            sx={{ letterSpacing: "0.08em", fontSize: "10.5px", fontWeight: 700, color: "text.secondary" }}
          >
            Account identification
          </Typography>
        </Stack>
        <Box sx={attrMappingRowSx(true)}>
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={1.5}
            sx={{ p: 1.5 }}
          >
            <Stack direction="row" alignItems="flex-start" spacing={1.25}>
              <Box sx={attrMappingFieldIconSx(true)}>
                <EmailIcon sx={{ fontSize: 17 }} />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {EMAIL_FIELD.label}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                  Used to find your account at login. Never used to change an existing
                  account&apos;s email.
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={0.25}>
              <Chip label="Required" sx={attrMappingRequiredChipSx} />
              <IconButton
                size="small"
                onClick={toggleEmailExpanded}
                aria-label={emailExpanded ? "Collapse Email" : "Expand Email"}
                aria-expanded={emailExpanded}
                sx={{
                  transform: emailExpanded ? "rotate(180deg)" : "none",
                  transition: "transform 150ms ease",
                }}
              >
                <ExpandMoreIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>
          <Collapse in={emailExpanded} unmountOnExit>
            <Divider />
            <Box sx={{ p: 1.5 }}>
              {renderModeBody(EMAIL_FIELD.key, EMAIL_FIELD.label, EMAIL_FIELD.helper)}
            </Box>
          </Collapse>
        </Box>
      </Box>

      <Box>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 1.75 }}>
          <Box>
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <SyncOutlinedIcon sx={attrMappingSectionIconSx} />
              <Typography
                variant="overline"
                sx={{ letterSpacing: "0.08em", fontSize: "10.5px", fontWeight: 700, color: "text.secondary" }}
              >
                Profile sync on login
              </Typography>
            </Stack>
            <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.25 }}>
              These toggles only affect people who already have an account. New users are always
              created from the attributes mapped here, even with every toggle off. If the identity
              provider doesn&apos;t send a value for a field you turn on, the existing value is kept.
            </Typography>
          </Box>
          {enabledSyncKeys.length > 0 && (
            <Link
              component="button"
              type="button"
              underline="hover"
              onClick={toggleExpandAll}
              sx={{ fontSize: "12.5px", fontWeight: 600, whiteSpace: "nowrap", mt: 0.25 }}
            >
              {allExpanded ? "Collapse all" : "Expand all"}
            </Link>
          )}
        </Stack>
        <Stack spacing={1.25}>
          {SYNC_ELIGIBLE_ATTRIBUTE_FIELDS.map(({ key, label, helper }) => {
            const enabled = enabledFields.includes(key);
            const expanded = Boolean(expandedFields[key]);
            const FieldIcon = FIELD_ICONS[key];
            return (
              <Box key={key} sx={attrMappingRowSx(enabled)}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ px: 1.5, py: enabled ? 1.25 : 0.75 }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.25}>
                    <Box sx={attrMappingFieldIconSx(enabled)}>
                      <FieldIcon sx={{ fontSize: 17 }} />
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: enabled ? "text.primary" : "text.secondary" }}
                    >
                      {label}
                    </Typography>
                    {errors[key] && !expanded && (
                      <Chip
                        size="small"
                        label="Needs attention"
                        sx={{ height: 20, fontSize: "10.5px", fontWeight: 700 }}
                        color="error"
                        variant="outlined"
                      />
                    )}
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={0.25}>
                    <Switch
                      size="small"
                      checked={enabled}
                      disabled={disabled}
                      onChange={(event) => toggleEnabled(key, event.target.checked)}
                      inputProps={{ "aria-label": `Sync ${label} on login` }}
                    />
                    {enabled && (
                      <IconButton
                        size="small"
                        onClick={() => toggleExpanded(key)}
                        aria-label={expanded ? `Collapse ${label}` : `Expand ${label}`}
                        aria-expanded={expanded}
                        sx={{
                          transform: expanded ? "rotate(180deg)" : "none",
                          transition: "transform 150ms ease",
                        }}
                      >
                        <ExpandMoreIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                </Stack>
                {enabled && (
                  <Collapse in={expanded} unmountOnExit>
                    <Divider />
                    <Box sx={{ p: 1.5 }}>{renderModeBody(key, label, helper)}</Box>
                  </Collapse>
                )}
              </Box>
            );
          })}
        </Stack>
      </Box>
    </Stack>
  );
};

export default AttributeMappingEditor;
